import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { db } from '../lib/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { Footer } from '../components/Footer';
import { MapPin, Phone, Clock, ArrowLeft, Image as ImageIcon, ChevronLeft, ChevronRight, X } from 'lucide-react';

export default function BranchDetail() {
  const { id } = useParams();
  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchBranch = async () => {
      try {
        const docRef = doc(db, 'branches', id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setBranch({ id: docSnap.id, ...docSnap.data() });
        }
      } catch (error) {
        console.error('Error fetching branch:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBranch();
  }, [id]);

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setSelectedImage(branch.images[index]);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    const newIndex = (currentImageIndex + 1) % branch.images.length;
    setCurrentImageIndex(newIndex);
    setSelectedImage(branch.images[newIndex]);
  };

  const prevImage = () => {
    const newIndex = (currentImageIndex - 1 + branch.images.length) % branch.images.length;
    setCurrentImageIndex(newIndex);
    setSelectedImage(branch.images[newIndex]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050000] pt-20 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-[#D90429] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!branch) {
    return (
      <div className="min-h-screen bg-[#050000] pt-20">
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl text-white mb-4">Filial tapılmadı</h1>
          <Link to="/branches" className="text-[#D90429] hover:underline">
            Bütün filiallara qayıt
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050000] pt-20" data-testid="branch-detail-page">
      {/* Header */}
      <section className="py-8 px-4 border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <Link 
            to="/branches" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Bütün filiallar
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-[#D90429]/10 rounded-xl flex items-center justify-center">
                <MapPin className="w-8 h-8 text-[#D90429]" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-cinzel font-bold text-white">
                  {branch.name}
                </h1>
                <p className="text-gray-400 text-lg">{branch.address}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {/* Phone Numbers */}
            {branch.phones && branch.phones.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-[#0F0F0F] border border-white/10 rounded-xl p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Phone className="w-6 h-6 text-[#D90429]" />
                  <h3 className="text-xl font-bold text-white">Əlaqə Nömrələri</h3>
                </div>
                <div className="space-y-3">
                  {branch.phones.map((phone, index) => (
                    <a
                      key={index}
                      href={`tel:${phone.number.replace(/\s/g, '')}`}
                      className="flex items-center gap-3 text-gray-300 hover:text-[#D90429] transition-colors"
                    >
                      <span className="text-sm text-gray-500">{phone.label || `Nömrə ${index + 1}`}:</span>
                      <span className="font-medium">{phone.number}</span>
                    </a>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Single phone fallback */}
            {(!branch.phones || branch.phones.length === 0) && branch.phone && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-[#0F0F0F] border border-white/10 rounded-xl p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Phone className="w-6 h-6 text-[#D90429]" />
                  <h3 className="text-xl font-bold text-white">Əlaqə</h3>
                </div>
                <a
                  href={`tel:${branch.phone.replace(/\s/g, '')}`}
                  className="text-gray-300 hover:text-[#D90429] transition-colors font-medium"
                >
                  {branch.phone}
                </a>
              </motion.div>
            )}

            {/* Working Hours */}
            {branch.workingHours && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-[#0F0F0F] border border-white/10 rounded-xl p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="w-6 h-6 text-[#D90429]" />
                  <h3 className="text-xl font-bold text-white">İş Saatları</h3>
                </div>
                <p className="text-gray-300 font-medium">{branch.workingHours}</p>
              </motion.div>
            )}

            {/* Google Maps */}
            {branch.mapUrl && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-[#0F0F0F] border border-white/10 rounded-xl p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <MapPin className="w-6 h-6 text-[#D90429]" />
                  <h3 className="text-xl font-bold text-white">Xəritə</h3>
                </div>
                
                {/* Embedded Google Map */}
                <div className="relative w-full h-64 sm:h-80 md:h-96 rounded-lg overflow-hidden mb-4">
                  <iframe
                    src={branch.mapUrl.includes('embed') ? branch.mapUrl : `https://www.google.com/maps?q=${encodeURIComponent(branch.address)}&output=embed`}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="absolute inset-0"
                  />
                </div>
                
                <a
                  href={branch.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#D90429] hover:bg-[#B00220] text-white px-4 py-2 rounded-lg transition-colors font-medium text-sm sm:text-base"
                >
                  <MapPin className="w-4 h-4" />
                  Google Maps-də aç
                </a>
              </motion.div>
            )}
          </div>

          {/* Description */}
          {branch.description && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-[#0F0F0F] border border-white/10 rounded-xl p-6 mb-12"
            >
              <h3 className="text-xl font-bold text-white mb-4">Haqqında</h3>
              <p className="text-gray-300 leading-relaxed">{branch.description}</p>
            </motion.div>
          )}

          {/* Image Gallery */}
          {branch.images && branch.images.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <ImageIcon className="w-6 h-6 text-[#D90429]" />
                <h3 className="text-2xl font-bold text-white">Şəkillər</h3>
                <span className="text-gray-500">({branch.images.length})</span>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {branch.images.map((image, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.1 * index }}
                    className="aspect-square rounded-xl overflow-hidden cursor-pointer group relative"
                    onClick={() => openLightbox(index)}
                  >
                    <img
                      src={image}
                      alt={`${branch.name} - Şəkil ${index + 1}`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                      <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity font-medium">
                        Böyüt
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* No Images */}
          {(!branch.images || branch.images.length === 0) && (
            <div className="text-center py-12 bg-[#0F0F0F] border border-white/10 rounded-xl">
              <ImageIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Bu filial üçün şəkil əlavə edilməyib</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white/70 hover:text-white p-2"
          >
            <X className="w-8 h-8" />
          </button>
          
          {branch.images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                className="absolute left-4 text-white/70 hover:text-white p-2"
              >
                <ChevronLeft className="w-10 h-10" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                className="absolute right-4 text-white/70 hover:text-white p-2"
              >
                <ChevronRight className="w-10 h-10" />
              </button>
            </>
          )}
          
          <img
            src={selectedImage}
            alt="Böyüdülmüş şəkil"
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          
          <div className="absolute bottom-4 text-white/70 text-sm">
            {currentImageIndex + 1} / {branch.images.length}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
